import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQueryClient } from 'react-query';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDataV4Service, serviceName } from '../service';
import { GetUnifiedThreadIO, ServiceQueries } from '../types';
import { useSMSDataV4InfiniteQuery, UseSMSDataV4InfiniteQueryEndpointArgs } from './use-sms-data-v4-infinite-query';

export const getGetUnifiedThreadQueryKey = (req: GetUnifiedThreadIO['input']) =>
  ['V4', 'GetUnifiedThread', req] as const;

type GetUnifiedThreadPageParams = {
  pageToken: GetUnifiedThreadIO['input']['pageToken'];
};
/**
 * A hook that returns an infinite query for the `GetUnifiedThread` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetUnifiedThreadQuery = <E = unknown, D = GetUnifiedThreadIO['output']>({
  options,
  request,
  ...args
}: UseSMSDataV4InfiniteQueryEndpointArgs<'GetUnifiedThread', E, D, GetUnifiedThreadPageParams>) =>
  useSMSDataV4InfiniteQuery<'GetUnifiedThread', E, D, GetUnifiedThreadPageParams>({
    endpointName: 'GetUnifiedThread',
    ...args,
    request: ({ pageParam }) => {
      // We must remove the focus data on request if paging, since it will supersede the pagination

      const { focusThreadItem, focusCreatedAt, ...filterWithoutFocus } = request.filter || {};

      const result: GetUnifiedThreadIO['input'] = {
        ...request,
        pageToken: pageParam?.pageToken,
        // Start with filter without focus parameters
        filter: filterWithoutFocus,
      };

      if (!pageParam) {
        // Re-add focus data if not paging (same as V3 logic)
        if (focusThreadItem || focusCreatedAt) {
          result.filter = {
            ...(result.filter || {}),
            ...(focusThreadItem && { focusThreadItem }),
            ...(focusCreatedAt && { focusCreatedAt }),
          };
        }
      }

      return result;
    },
    options: {
      ...options,
      getNextPageParam: ({ olderPageToken }): GetUnifiedThreadPageParams | undefined => {
        return olderPageToken
          ? {
              pageToken: olderPageToken,
            }
          : undefined;
      },
      getPreviousPageParam: ({ newerPageToken }): GetUnifiedThreadPageParams | undefined => {
        return newerPageToken
          ? {
              pageToken: newerPageToken,
            }
          : undefined;
      },
      enabled: hasSchemaACL(request.locationId, Permission.SMS_INBOX_MANAGE) && (options?.enabled ?? true),
    },
  });

export const useFetchGetUnifiedThread = () => {
  const queryClient = useQueryClient();

  const fetchGetUnifiedThread = async <E = unknown, D = GetUnifiedThreadIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSMSDataV4InfiniteQueryEndpointArgs<'GetUnifiedThread', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetUnifiedThreadIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetUnifiedThread'>>
    >;
  }) => {
    try {
      const response = await queryClient.fetchQuery<
        GetUnifiedThreadIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetUnifiedThread'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetUnifiedThread'>({
          serviceName,
          endpointName: 'GetUnifiedThread',
          request,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSMSDataV4Service.GetUnifiedThread(request, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetUnifiedThread;
};
